import { type FinancialStatementType } from '../../../types/reports';
import { useCurrency } from '../../../hooks/useCurrency';
import { CurrencyCode } from '../../../config/currency';
import { TrialBalance } from '../statements/TrialBalance';

interface FinancialStatementPreviewProps {
  type: FinancialStatementType;
  data: any;
  currency?: CurrencyCode;
}

export function FinancialStatementPreview({ type, data, currency }: FinancialStatementPreviewProps) {
  const { format } = useCurrency();
  
  // Formater un montant avec gestion des valeurs manquantes
  const formatAmount = (amount: number | undefined | null): string => {
    if (amount === undefined || amount === null || isNaN(amount)) return 'N/A';
    return format(amount, currency);
  };

  // Obtenir un tableau avec fallback vide
  const getArray = (obj: any, key: string): any[] => {
    const val = obj?.[key];
    return Array.isArray(val) ? val : [];
  };

  // Obtenir un objet avec fallback
  const getObject = (obj: any, key: string): any => {
    return obj?.[key] || {};
  };

  if (!data) {
    return (
      <div className="text-center py-8 text-gray-500">
        Aucune donnée disponible
      </div>
    );
  }

  const renderBalanceSheet = () => {
    // ---- normalise : accepte à la fois la réponse brute (actif/passif) et le format adapté (fixedAssets/…) ----
    let fixedAssets: any, currentAssets: any, treasuryAssets: any,
        equity: any, financialDebts: any, currentLiabilities: any, treasuryLiabilities: any,
        totalAssets: any, totalLiabilities: any;

    const isRawApi = data.actif !== undefined || data.passif !== undefined;
    if (isRawApi) {
      // ---- format brut backend SYSCOHADA (noms de champs réels) ----
      const immo       = data.actif?.immobilisations    ?? {};
      const circ       = data.actif?.actifCirculant      ?? {};
      const tresoActif = data.actif?.tresorerieActif;           // Top-level — séparé de actifCirculant
      const caps       = data.passif?.capitauxPropresNets ?? {}; // ex-capitauxPropres
      const dettesFin  = data.passif?.dettesFinancieresLT ?? {}; // ex-dettes.emprunts/dettesCredit
      const passifCirc = data.passif?.passifCirculant     ?? {}; // ex-dettes.fournisseurs/…

      const wrapActif  = (item: any) => item ? [item] : [];
      const wrapPassif = (item: any) => item ? [item] : [];

      fixedAssets = {
        intangibleAssets: [
          ...wrapActif(immo.chargesImmobilisees),
          ...wrapActif(immo.incorporelles),
        ],
        tangibleAssets:   wrapActif(immo.corporelles),
        financialAssets:  [
          ...wrapActif(immo.financieres),
          ...wrapActif(immo.avancesAcomptes),
        ],
        total: immo.total,
      };
      // Actif circulant : stocks + créances + régularisation (hors trésorerie)
      const circItems = [circ.stocks, circ.creancesClients, circ.creancesFiscales, circ.autresCreances, circ.regularisationActif].filter(Boolean);
      const circBrut  = circItems.reduce((s: number, i: any) => s + (i?.brut  ?? 0), 0);
      const circAmort = circItems.reduce((s: number, i: any) => s + (i?.amort ?? 0), 0);
      const circNet   = circItems.reduce((s: number, i: any) => s + (i?.net   ?? 0), 0);
      currentAssets = {
        inventory:   wrapActif(circ.stocks),
        receivables: wrapActif(circ.creancesClients),
        other:       [
          ...wrapActif(circ.creancesFiscales),
          ...wrapActif(circ.autresCreances),
          ...wrapActif(circ.regularisationActif),
        ],
        total: circ.total ?? { code: 'A2', label: 'Total Actif circulant', brut: circBrut, amort: circAmort, net: circNet },
      };
      // Trésorerie actif — niveau supérieur (pas dans actifCirculant)
      treasuryAssets = {
        banks:       tresoActif ? [tresoActif] : [],
        investments: [],
        cashOnHand:  [],
        total: tresoActif ?? { code: 'AT', label: 'Trésorerie Actif', brut: 0, amort: 0, net: 0 },
      };
      equity = {
        capital:  wrapPassif(caps.capital),
        reserves: [caps.reserves, caps.subventionsInvestissement, caps.provisionsReglementees].filter(Boolean),
        result:   [caps.reportANouveau, caps.resultat].filter(Boolean),
        other:    [],
        total: caps.total,
      };
      const finNet = (dettesFin.emprunts?.net ?? 0) + (dettesFin.dettesCreditBail?.net ?? 0) + (dettesFin.dettesParticipations?.net ?? 0);
      financialDebts = {
        loans:   wrapPassif(dettesFin.emprunts),
        leasing: wrapPassif(dettesFin.dettesCreditBail),    // ex-dettesCredit
        other:   wrapPassif(dettesFin.dettesParticipations),
        total: dettesFin.total ?? { code: 'P2', label: 'Dettes financières LT', net: finNet },
      };
      currentLiabilities = {
        suppliers: wrapPassif(passifCirc.fournisseurs),
        fiscal:    wrapPassif(passifCirc.dettesFiscales),
        social:    [],
        other:     [passifCirc.autresDettes, passifCirc.regularisationPassif].filter(Boolean), // ex-compteRegularisation
        total: passifCirc.total,
      };
      // Trésorerie passif — top-level dans la réponse backend
      const tresoPassif = data.passif?.tresoreriePassif;
      treasuryLiabilities = {
        banks: tresoPassif ? [tresoPassif] : [],
        other: [],
        total: tresoPassif ?? { code: 'PT', label: 'Trésorerie Passif', net: 0 },
      };
      totalAssets      = data.actif?.grandTotal;
      totalLiabilities = data.passif?.grandTotal;
    } else {
      // ---- format déjà adapté ----
      fixedAssets         = getObject(data, 'fixedAssets');
      currentAssets       = getObject(data, 'currentAssets');
      treasuryAssets      = getObject(data, 'treasuryAssets');
      equity              = getObject(data, 'equity');
      financialDebts      = getObject(data, 'financialDebts');
      currentLiabilities  = getObject(data, 'currentLiabilities');
      treasuryLiabilities = getObject(data, 'treasuryLiabilities');
      totalAssets         = getObject(data, 'totalAssets');
      totalLiabilities    = getObject(data, 'totalLiabilities');
    }

    // filtre les items placeholders générés par l'adaptateur (code vide = ligne sans donnée réelle)
    const nonEmpty = (items: any[]): any[] => items.filter((i: any) => i?.code || i?.label);

    // Formater un montant bilan : 0 est une valeur réelle à afficher
    const fmtBS = (v: any) => (v === undefined || v === null || isNaN(v)) ? '—' : format(v, currency);

    // Fonction pour rendre une ligne actif (6 colonnes)
    const renderRow = (item: any, key: string | number) => (
      <tr key={key}>
        <td className="px-4 py-2 border-r border-gray-200">{item?.code || '—'}</td>
        <td className="px-4 py-2 border-r border-gray-200">{item?.label || '—'}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{fmtBS(item?.brut)}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{fmtBS(item?.amort)}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{fmtBS(item?.net)}</td>
        <td className="px-4 py-2 text-right">{fmtBS(item?.netN1)}</td>
      </tr>
    );

    // Fonction pour rendre un total actif
    const renderTotal = (label: string, total: any, className: string = 'bg-gray-100 font-medium') => (
      <tr className={className}>
        <td colSpan={2} className="px-4 py-2 border-r border-gray-200">{label}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{fmtBS(total?.brut)}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{fmtBS(total?.amort)}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{fmtBS(total?.net)}</td>
        <td className="px-4 py-2 text-right">{fmtBS(total?.netN1)}</td>
      </tr>
    );

    // Fonction pour rendre une ligne passif (4 colonnes)
    const renderPassifRow = (item: any, key: string | number) => (
      <tr key={key}>
        <td className="px-4 py-2 border-r border-gray-200">{item?.code || '—'}</td>
        <td className="px-4 py-2 border-r border-gray-200">{item?.label || '—'}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{fmtBS(item?.net)}</td>
        <td className="px-4 py-2 text-right">{fmtBS(item?.netN1)}</td>
      </tr>
    );

    const renderPassifTotal = (label: string, total: any, className: string = 'bg-gray-100 font-medium') => (
      <tr className={className}>
        <td colSpan={2} className="px-4 py-2 border-r border-gray-200">{label}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{fmtBS(total?.net)}</td>
        <td className="px-4 py-2 text-right">{fmtBS(total?.netN1)}</td>
      </tr>
    );

    return (
      <div className="space-y-8">
        {/* ACTIF */}
        <div>
          <h3 className="text-lg font-bold mb-4 print:text-black">ACTIF</h3>
          <table className="min-w-full divide-y divide-gray-200 border border-gray-200 print:border-gray-300">
            <thead className="bg-muted print:bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-left border-b border-r border-gray-200">Code</th>
                <th className="px-4 py-2 text-left border-b border-r border-gray-200">Libellé</th>
                <th className="px-4 py-2 text-right border-b border-r border-gray-200">Brut</th>
                <th className="px-4 py-2 text-right border-b border-r border-gray-200">Amort/Prov</th>
                <th className="px-4 py-2 text-right border-b border-r border-gray-200">Net</th>
                <th className="px-4 py-2 text-right border-b border-gray-200">Net N-1</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {/* Actif immobilisé */}
              <tr className="bg-gray-50 font-medium">
                <td colSpan={6} className="px-4 py-2">Actif immobilisé</td>
              </tr>
              {nonEmpty(getArray(fixedAssets, 'intangibleAssets')).length > 0 ? (
                nonEmpty(getArray(fixedAssets, 'intangibleAssets')).map((item: any, idx: number) => renderRow(item, `intang-${idx}`))
              ) : (
                <tr><td colSpan={6} className="px-4 py-2 text-gray-400 italic">Aucune immobilisation incorporelle</td></tr>
              )}
              {nonEmpty(getArray(fixedAssets, 'tangibleAssets')).map((item: any, idx: number) => renderRow(item, `tang-${idx}`))}
              {nonEmpty(getArray(fixedAssets, 'financialAssets')).map((item: any, idx: number) => renderRow(item, `fin-${idx}`))}
              {renderTotal('Total Actif immobilisé', fixedAssets.total)}

              {/* Actif circulant */}
              <tr className="bg-gray-50 font-medium">
                <td colSpan={6} className="px-4 py-2">Actif circulant</td>
              </tr>
              {nonEmpty(getArray(currentAssets, 'inventory')).length > 0 ? (
                nonEmpty(getArray(currentAssets, 'inventory')).map((item: any, idx: number) => renderRow(item, `inv-${idx}`))
              ) : (
                <tr><td colSpan={6} className="px-4 py-2 text-gray-400 italic">Aucun stock</td></tr>
              )}
              {nonEmpty(getArray(currentAssets, 'receivables')).map((item: any, idx: number) => renderRow(item, `rec-${idx}`))}
              {nonEmpty(getArray(currentAssets, 'other')).map((item: any, idx: number) => renderRow(item, `other-${idx}`))}
              {renderTotal('Total Actif circulant', currentAssets.total)}

              {/* Trésorerie Actif */}
              <tr className="bg-gray-50 font-medium">
                <td colSpan={6} className="px-4 py-2">Trésorerie Actif</td>
              </tr>
              {nonEmpty(getArray(treasuryAssets, 'banks')).map((item: any, idx: number) => renderRow(item, `bank-${idx}`))}
              {nonEmpty(getArray(treasuryAssets, 'cashOnHand')).map((item: any, idx: number) => renderRow(item, `cash-${idx}`))}
              {renderTotal('Total Trésorerie Actif', treasuryAssets.total)}

              {/* Total Actif */}
              {renderTotal('TOTAL ACTIF', totalAssets, 'bg-primary print:bg-gray-700 text-white print:text-white font-bold')}
            </tbody>
          </table>
        </div>

        {/* PASSIF */}
        <div>
          <h3 className="text-lg font-bold mb-4 print:text-black">PASSIF</h3>
          <table className="min-w-full divide-y divide-gray-200 border border-gray-200 print:border-gray-300">
            <thead className="bg-muted print:bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-left border-b border-r border-gray-200">Code</th>
                <th className="px-4 py-2 text-left border-b border-r border-gray-200">Libellé</th>
                <th className="px-4 py-2 text-right border-b border-r border-gray-200">Net</th>
                <th className="px-4 py-2 text-right border-b border-gray-200">Net N-1</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {/* Capitaux propres */}
              <tr className="bg-gray-50 font-medium">
                <td colSpan={4} className="px-4 py-2">Capitaux propres</td>
              </tr>
              {nonEmpty(getArray(equity, 'capital')).length > 0 ? (
                nonEmpty(getArray(equity, 'capital')).map((item: any, idx: number) => renderPassifRow(item, `cap-${idx}`))
              ) : (
                <tr><td colSpan={4} className="px-4 py-2 text-gray-400 italic">Aucun capital</td></tr>
              )}
              {nonEmpty(getArray(equity, 'reserves')).map((item: any, idx: number) => renderPassifRow(item, `res-${idx}`))}
              {nonEmpty(getArray(equity, 'result')).map((item: any, idx: number) => renderPassifRow(item, `result-${idx}`))}
              {renderPassifTotal('Total Capitaux propres', equity.total)}

              {/* Dettes financières */}
              <tr className="bg-gray-50 font-medium">
                <td colSpan={4} className="px-4 py-2">Dettes financières</td>
              </tr>
              {nonEmpty(getArray(financialDebts, 'loans')).map((item: any, idx: number) => renderPassifRow(item, `loan-${idx}`))}
              {nonEmpty(getArray(financialDebts, 'leasing')).map((item: any, idx: number) => renderPassifRow(item, `lease-${idx}`))}
              {renderPassifTotal('Total Dettes financières', financialDebts.total)}

              {/* Dettes circulantes */}
              <tr className="bg-gray-50 font-medium">
                <td colSpan={4} className="px-4 py-2">Passif circulant</td>
              </tr>
              {nonEmpty(getArray(currentLiabilities, 'suppliers')).map((item: any, idx: number) => renderPassifRow(item, `supp-${idx}`))}
              {nonEmpty(getArray(currentLiabilities, 'fiscal')).map((item: any, idx: number) => renderPassifRow(item, `fisc-${idx}`))}
              {nonEmpty(getArray(currentLiabilities, 'social')).map((item: any, idx: number) => renderPassifRow(item, `soc-${idx}`))}
              {renderPassifTotal('Total Passif circulant', currentLiabilities.total)}

              {/* Trésorerie Passif */}
              {treasuryLiabilities && nonEmpty(getArray(treasuryLiabilities, 'banks')).length > 0 && (
                <>
                  <tr className="bg-gray-50 font-medium">
                    <td colSpan={4} className="px-4 py-2">Trésorerie Passif</td>
                  </tr>
                  {nonEmpty(getArray(treasuryLiabilities, 'banks')).map((item: any, idx: number) => renderPassifRow(item, `tp-${idx}`))}
                  {renderPassifTotal('Total Trésorerie Passif', treasuryLiabilities.total)}
                </>
              )}

              {/* Total Passif */}
              {renderPassifTotal('TOTAL PASSIF', totalLiabilities, 'bg-primary print:bg-gray-700 text-white print:text-white font-bold')}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderIncomeStatement = () => {
    // accepte à la fois le format brut backend et le format adapté
    const isRawIncome = data.produitsExploitation !== undefined || data.chargesExploitation !== undefined;

    let operatingIncome: any, operatingExpenses: any, operatingResult: any,
        financialResult: any, ordinaryResult: any, haoResult: any,
        taxes: any, netResult: any;

    const toItem = (o: any) => o ? { code: o.code, label: o.label, current: o.amount ?? o.current ?? 0 } : null;
    const toArr  = (...objs: any[]) => objs.map(toItem).filter(Boolean) as any[];

    if (isRawIncome) {
      const pe  = data.produitsExploitation  ?? {};
      const ce  = data.chargesExploitation   ?? {};
      const sig = data.sig                    ?? {};
      const rf  = data.resultatFinancier      ?? {};
      const hao = data.resultatHAO            ?? {};
      operatingIncome = {
        sales:      toArr(pe.ventesMarchandises, pe.ventesProduitsVendus),  // ex-productionVendue
        production: toArr(pe.travauxEtServices, pe.activitesAnnexes),       // ex-productionStockee/Immobilisee
        grants:     toArr(pe.subventionsExploitation),
        other:      toArr(pe.autresProduits, pe.reprisesSurProvisions, pe.transfertsDeCharges),
        total: toItem(pe.total),
      };
      operatingExpenses = {
        purchases:    toArr(ce.achatsConsommes),              // ex-achatsMarchandises + variationStocks
        services:     toArr(ce.transportsConsommes, ce.servicesExtA, ce.servicesExtB), // ex-servicesExterieurs/autresServices
        taxes:        toArr(ce.impotsTaxes),
        personnel:    toArr(ce.chargesPersonnel),
        depreciation: toArr(ce.dotationsAmort),              // ex-dotationsAmortissements
        other:        toArr(ce.autresCharges),
        total: toItem(ce.total),
      };
      operatingResult = toItem(data.resultatExploitation);
      financialResult = toItem(rf.total);
      ordinaryResult  = toItem(data.resultatCourant);
      haoResult       = toItem(hao.total);
      taxes     = toItem(data.impotsSurResultat);
      netResult = toItem(data.resultatNet);
      // Soldes Intermédiaires de Gestion — injectés si présents
      if (sig.margeCommerciale || sig.valeurAjoutee || sig.excedentBrutExploitation) {
        (operatingIncome as any).__sig = {
          margeCommerciale:         toItem(sig.margeCommerciale),
          valeurAjoutee:            toItem(sig.valeurAjoutee),
          excedentBrutExploitation: toItem(sig.excedentBrutExploitation),
        };
      }
    } else {
      operatingIncome   = getObject(data, 'operatingIncome');
      operatingExpenses = getObject(data, 'operatingExpenses');
      operatingResult   = getObject(data, 'operatingResult');
      financialResult   = getObject(data, 'financialResult');
      ordinaryResult    = getObject(data, 'ordinaryResult');
      haoResult         = getObject(data, 'extraordinaryResult');
      taxes     = getObject(data, 'taxes');
      netResult = getObject(data, 'netResult');
    }

    const fmtAmt = (v: any) => (v === undefined || v === null || isNaN(v)) ? '—' : format(v, currency);

    const renderIncomeRow = (item: any, key: string | number) => (
      <tr key={key}>
        <td className="px-4 py-2 border-r border-gray-200">{item?.code || '—'}</td>
        <td className="px-4 py-2 border-r border-gray-200">{item?.label || '—'}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{fmtAmt(item?.current ?? item?.amount)}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{fmtAmt(item?.previous)}</td>
        <td className="px-4 py-2 text-right">{item?.variation !== undefined ? `${item.variation}%` : '—'}</td>
      </tr>
    );

    const renderIncomeTotal = (label: string, total: any, className: string = 'bg-gray-100 font-medium') => (
      <tr className={className}>
        <td colSpan={2} className="px-4 py-2 border-r border-gray-200">{label}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{fmtAmt(total?.current ?? total?.amount)}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{fmtAmt(total?.previous)}</td>
        <td className="px-4 py-2 text-right">{total?.variation !== undefined ? `${total.variation}%` : '—'}</td>
      </tr>
    );

    return (
      <div className="space-y-8">
        <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-2 text-left border-b border-r border-gray-200">Code</th>
              <th className="px-4 py-2 text-left border-b border-r border-gray-200">Libellé</th>
              <th className="px-4 py-2 text-right border-b border-r border-gray-200">N</th>
              <th className="px-4 py-2 text-right border-b border-r border-gray-200">N-1</th>
              <th className="px-4 py-2 text-right border-b border-gray-200">Variation</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {/* Produits d'exploitation */}
            <tr className="bg-gray-50 font-medium">
              <td colSpan={5} className="px-4 py-2">Produits d'exploitation</td>
            </tr>
            {getArray(operatingIncome, 'sales').length > 0 ? (
              getArray(operatingIncome, 'sales').map((item: any, idx: number) => renderIncomeRow(item, `sales-${idx}`))
            ) : (
              <tr><td colSpan={5} className="px-4 py-2 text-gray-400 italic">Aucune vente enregistrée</td></tr>
            )}
            {getArray(operatingIncome, 'production').map((item: any, idx: number) => renderIncomeRow(item, `prod-${idx}`))}
            {getArray(operatingIncome, 'grants').map((item: any, idx: number) => renderIncomeRow(item, `grant-${idx}`))}
            {getArray(operatingIncome, 'other').map((item: any, idx: number) => renderIncomeRow(item, `other-inc-${idx}`))}
            {renderIncomeTotal('Total Produits d\'exploitation', operatingIncome.total)}

            {/* Charges d'exploitation */}
            <tr className="bg-gray-50 font-medium">
              <td colSpan={5} className="px-4 py-2">Charges d'exploitation</td>
            </tr>
            {getArray(operatingExpenses, 'purchases').map((item: any, idx: number) => renderIncomeRow(item, `purch-${idx}`))}
            {getArray(operatingExpenses, 'services').map((item: any, idx: number) => renderIncomeRow(item, `serv-${idx}`))}
            {getArray(operatingExpenses, 'taxes').map((item: any, idx: number) => renderIncomeRow(item, `tax-${idx}`))}
            {getArray(operatingExpenses, 'personnel').map((item: any, idx: number) => renderIncomeRow(item, `pers-${idx}`))}
            {getArray(operatingExpenses, 'depreciation').map((item: any, idx: number) => renderIncomeRow(item, `depr-${idx}`))}
            {getArray(operatingExpenses, 'other').map((item: any, idx: number) => renderIncomeRow(item, `other-exp-${idx}`))}
            {renderIncomeTotal('Total Charges d\'exploitation', operatingExpenses.total)}

            {/* Soldes Intermédiaires de Gestion SYSCOHADA */}
            {(operatingIncome as any).__sig && (() => {
              const sig = (operatingIncome as any).__sig;
              return (
                <>
                  <tr className="bg-yellow-50 font-medium dark:bg-yellow-900/20">
                    <td colSpan={5} className="px-4 py-2 text-yellow-800 dark:text-yellow-300">Soldes Intermédiaires de Gestion</td>
                  </tr>
                  {sig.margeCommerciale         && renderIncomeTotal('Marge commerciale (MC)', sig.margeCommerciale, 'bg-yellow-50 dark:bg-yellow-900/10')}
                  {sig.valeurAjoutee            && renderIncomeTotal('Valeur ajoutée (VA)', sig.valeurAjoutee, 'bg-yellow-50 dark:bg-yellow-900/10')}
                  {sig.excedentBrutExploitation && renderIncomeTotal('Excédent brut d\'exploitation (EBE)', sig.excedentBrutExploitation, 'bg-yellow-50 dark:bg-yellow-900/10')}
                </>
              );
            })()}

            {/* Résultat d'exploitation */}
            {renderIncomeTotal('RÉSULTAT D\'EXPLOITATION', operatingResult, 'bg-blue-100 font-bold')}

            {/* Résultat financier */}
            {renderIncomeTotal('Résultat financier', financialResult)}

            {/* Résultat courant */}
            {renderIncomeTotal('Résultat des activités ordinaires (RAO)', ordinaryResult)}

            {/* HAO */}
            {haoResult && renderIncomeTotal('Résultat HAO', haoResult)}

            {/* Impôts */}
            {taxes && renderIncomeTotal('Impôts sur le résultat', taxes, 'bg-gray-50')}

            {/* Résultat net */}
            {renderIncomeTotal('RÉSULTAT NET', netResult, 'bg-primary print:bg-gray-700 text-white print:text-white font-bold')}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCashFlow = () => {
    // accepte le format brut backend ET le format adapté
    const isRawCash = data.operatingActivities !== undefined && data.operatingActivities.netCashFlow !== undefined
                      && !data.operatingActivities.adjustments;

    let operatingActivities: any, investingActivities: any, financingActivities: any,
        netCashChange: any, openingCash: any, closingCash: any;

    const mkItem = (code: string, label: string, amount: number) =>
      ({ code, label, current: amount });

    if (isRawCash) {
      const oa = data.operatingActivities ?? {};
      const ia = data.investingActivities  ?? {};
      const fa = data.financingActivities  ?? {};
      const toI = (o: any, code: string) => o ? mkItem(o.code ?? code, o.label, o.amount ?? 0) : null;
      operatingActivities = {
        netResult:    toI(oa.netResult, 'RN'),
        adjustments:  [oa.dotations, oa.reprises].map((o: any, i: number) => toI(o, `ADJ${i}`)).filter(Boolean),
        workingCapital: [
          toI(oa.variationStocks, 'VS'), toI(oa.variationCreancesClients, 'VCC'),
          toI(oa.variationAutresCreances, 'VAC'), toI(oa.variationFournisseurs, 'VF'),
          toI(oa.variationDettesFiscales, 'VDF'),
        ].filter(Boolean),
        total: mkItem('OA', "Flux net des activités opérationnelles", oa.netCashFlow ?? 0),
      };
      investingActivities = {
        acquisitions: [toI(ia.acquisitionsImmobilisations, 'AI')].filter(Boolean),
        disposals:    [toI(ia.produitsCessions, 'PC')].filter(Boolean),
        total: mkItem('IA', "Flux net des activités d'investissement", ia.netCashFlow ?? 0),
      };
      financingActivities = {
        capital:    [toI(fa.variationCapitaux, 'VC')].filter(Boolean),
        loans:      [toI(fa.variationEmprunts, 'VE')].filter(Boolean),
        repayments: [],
        dividends:  [toI(fa.dividendes, 'DIV')].filter(Boolean),
        total: mkItem('FA', 'Flux net des activités de financement', fa.netCashFlow ?? 0),
      };
      netCashChange = mkItem('NCC', 'Variation nette de trésorerie', data.netChangeInCash ?? 0);
      openingCash   = mkItem('OC',  "Trésorerie d'ouverture",         data.beginningCashBalance ?? 0);
      closingCash   = mkItem('CC',  'Trésorerie de clôture',           data.endingCashBalance ?? 0);
    } else {
      operatingActivities = getObject(data, 'operatingActivities');
      investingActivities = getObject(data, 'investingActivities');
      financingActivities = getObject(data, 'financingActivities');
      netCashChange = getObject(data, 'netCashChange');
      openingCash   = getObject(data, 'openingCash');
      closingCash   = getObject(data, 'closingCash');
    }

    const fmtCash = (v: any) => (v === undefined || v === null || isNaN(v)) ? '—' : format(v, currency);
    const getAmount = (item: any) => item?.current ?? item?.amount ?? null;
    const getPrevious = (item: any) => item?.previous ?? null;

    const renderCashRow = (item: any, key: string | number) => (
      <tr key={key}>
        <td className="px-4 py-2 border-r border-gray-200">{item?.code || '—'}</td>
        <td className="px-4 py-2 border-r border-gray-200">{item?.label || '—'}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{fmtCash(getAmount(item))}</td>
        <td className="px-4 py-2 text-right">{fmtCash(getPrevious(item))}</td>
      </tr>
    );

    const renderCashTotal = (label: string, total: any, className: string = 'bg-gray-100 font-medium') => (
      <tr className={className}>
        <td colSpan={2} className="px-4 py-2 border-r border-gray-200">{label}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{fmtCash(getAmount(total))}</td>
        <td className="px-4 py-2 text-right">{fmtCash(getPrevious(total))}</td>
      </tr>
    );

    return (
      <div className="space-y-8">
        <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-2 text-left border-b border-r border-gray-200">Code</th>
              <th className="px-4 py-2 text-left border-b border-r border-gray-200">Libellé</th>
              <th className="px-4 py-2 text-right border-b border-r border-gray-200">N</th>
              <th className="px-4 py-2 text-right border-b border-gray-200">N-1</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {/* Flux d'exploitation */}
            <tr className="bg-gray-50 font-medium">
              <td colSpan={4} className="px-4 py-2">Flux de trésorerie liés à l'exploitation</td>
            </tr>
            {renderCashRow(operatingActivities.netResult, 'net-result')}
            {getArray(operatingActivities, 'adjustments').map((item: any, idx: number) => renderCashRow(item, `adj-${idx}`))}
            {getArray(operatingActivities, 'workingCapital').map((item: any, idx: number) => renderCashRow(item, `wc-${idx}`))}
            {renderCashTotal('Total Flux d\'exploitation', operatingActivities.total)}

            {/* Flux d'investissement */}
            <tr className="bg-gray-50 font-medium">
              <td colSpan={4} className="px-4 py-2">Flux de trésorerie liés à l'investissement</td>
            </tr>
            {getArray(investingActivities, 'acquisitions').map((item: any, idx: number) => renderCashRow(item, `acq-${idx}`))}
            {getArray(investingActivities, 'disposals').map((item: any, idx: number) => renderCashRow(item, `disp-${idx}`))}
            {renderCashTotal('Total Flux d\'investissement', investingActivities.total)}

            {/* Flux de financement */}
            <tr className="bg-gray-50 font-medium">
              <td colSpan={4} className="px-4 py-2">Flux de trésorerie liés au financement</td>
            </tr>
            {getArray(financingActivities, 'capital').map((item: any, idx: number) => renderCashRow(item, `fcap-${idx}`))}
            {getArray(financingActivities, 'loans').map((item: any, idx: number) => renderCashRow(item, `floan-${idx}`))}
            {getArray(financingActivities, 'repayments').map((item: any, idx: number) => renderCashRow(item, `frep-${idx}`))}
            {getArray(financingActivities, 'dividends').map((item: any, idx: number) => renderCashRow(item, `fdiv-${idx}`))}
            {renderCashTotal('Total Flux de financement', financingActivities.total)}

            {/* Variation de trésorerie */}
            {renderCashTotal('VARIATION DE TRÉSORERIE', netCashChange, 'bg-primary print:bg-gray-700 text-white print:text-white font-bold')}

            {/* Trésorerie d'ouverture et de clôture */}
            {renderCashTotal('Trésorerie d\'ouverture', openingCash)}
            {renderCashTotal('Trésorerie de clôture', closingCash, 'bg-green-100 font-bold')}
          </tbody>
        </table>
      </div>
    );
  };

  const renderEquityChanges = () => {
    // Format réel backend : { components: [...], totals: { opening, movement, closing } }
    const components = Array.isArray(data.components) ? data.components
                     : Array.isArray(data.equityComponents) ? data.equityComponents
                     : [];
    const totals = data.totals || data.totalEquity || {};

    return (
      <div className="space-y-8">
        <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-2 text-left border-b border-r border-gray-200">Composante</th>
              <th className="px-4 py-2 text-right border-b border-r border-gray-200">Solde d'ouverture</th>
              <th className="px-4 py-2 text-right border-b border-r border-gray-200">Mouvements</th>
              <th className="px-4 py-2 text-right border-b border-gray-200">Solde de clôture</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {components.length > 0 ? (
              components.map((comp: any, idx: number) => (
                <tr key={idx}>
                  <td className="px-4 py-2 border-r border-gray-200">{comp?.label || 'N/A'}</td>
                  <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(comp?.opening ?? comp?.openingBalance)}</td>
                  <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(comp?.movement ?? comp?.changes)}</td>
                  <td className="px-4 py-2 text-right">{formatAmount(comp?.closing ?? comp?.closingBalance)}</td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={4} className="px-4 py-2 text-gray-400 italic text-center">Aucune composante de capitaux propres</td></tr>
            )}
            <tr className="bg-primary text-white font-bold">
              <td className="px-4 py-2 border-r border-gray-200">TOTAL CAPITAUX PROPRES</td>
              <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(totals?.opening ?? totals?.openingBalance)}</td>
              <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(totals?.movement ?? totals?.changes)}</td>
              <td className="px-4 py-2 text-right">{formatAmount(totals?.closing ?? totals?.closingBalance)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  const renderNotes = () => {
    return (
      <div className="space-y-4">
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h4 className="font-bold mb-2">Notes annexes</h4>
          {data.notes ? (
            <div className="prose max-w-none">
              {typeof data.notes === 'string' ? (
                <p>{data.notes}</p>
              ) : Array.isArray(data.notes) ? (
                data.notes.map((note: any, idx: number) => (
                  <div key={idx} className="mb-4">
                    <h5 className="font-medium">{note.title || `Note ${idx + 1}`}</h5>
                    <p>{note.content || 'N/A'}</p>
                  </div>
                ))
              ) : (
                <pre className="text-sm overflow-auto">{JSON.stringify(data, null, 2)}</pre>
              )}
            </div>
          ) : (
            <p className="text-gray-500 italic">Aucune note disponible</p>
          )}
        </div>
      </div>
    );
  };

  const renderGenericReport = () => {
    return (
      <div className="space-y-4">
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h4 className="font-bold mb-2">Données du rapport ({type})</h4>
          <pre className="text-sm overflow-auto bg-white p-4 rounded border">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      </div>
    );
  };

  const renderJournalBook = () => {
    const journals = getArray(data, 'journals');
    const grandTotals = getObject(data, 'grandTotals');
    const metadata = getObject(data, 'metadata');

    return (
      <div className="space-y-4">
        {/* En-tête */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
          <h3 className="text-lg font-bold text-blue-900 dark:text-blue-100">Livre Journal</h3>
          {metadata.startDate && metadata.endDate && (
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Période : {new Date(metadata.startDate).toLocaleDateString('fr-FR')} — {new Date(metadata.endDate).toLocaleDateString('fr-FR')}
            </p>
          )}
        </div>

        {/* Écritures */}
        {journals.length === 0 ? (
          <p className="text-gray-500 italic text-center py-4">Aucune écriture pour cette période</p>
        ) : (
          journals.map((entry: any, idx: number) => (
            <div key={entry.id || idx} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              {/* En-tête de l'écriture */}
              <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 flex justify-between items-center">
                <div>
                  <span className="font-semibold text-foreground">{entry.reference || `#${idx + 1}`}</span>
                  <span className="ml-3 text-sm text-muted-foreground">{entry.journalType}</span>
                  <span className="ml-3 text-sm text-muted-foreground">
                    {entry.date ? new Date(entry.date).toLocaleDateString('fr-FR') : ''}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">{entry.description}</span>
              </div>
              {/* Lignes */}
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800/50">
                    <th className="text-left px-4 py-1.5 font-medium">Compte</th>
                    <th className="text-left px-4 py-1.5 font-medium">Libellé</th>
                    <th className="text-right px-4 py-1.5 font-medium">Débit</th>
                    <th className="text-right px-4 py-1.5 font-medium">Crédit</th>
                    <th className="text-center px-4 py-1.5 font-medium">Lettrage</th>
                  </tr>
                </thead>
                <tbody>
                  {getArray(entry, 'lines').map((line: any, lIdx: number) => (
                    <tr key={lIdx} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="px-4 py-1.5 font-mono text-xs">{line.accountCode}</td>
                      <td className="px-4 py-1.5">{line.description || line.accountName}</td>
                      <td className="px-4 py-1.5 text-right">{line.debit ? formatAmount(line.debit) : ''}</td>
                      <td className="px-4 py-1.5 text-right">{line.credit ? formatAmount(line.credit) : ''}</td>
                      <td className="px-4 py-1.5 text-center">
                        {line.matchingNumber ? (
                          <span className="text-xs font-mono bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-1.5 py-0.5 rounded">{line.matchingNumber}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 dark:bg-gray-800 font-semibold">
                    <td colSpan={2} className="px-4 py-1.5 text-right">Total</td>
                    <td className="px-4 py-1.5 text-right">{formatAmount(entry.totalDebit)}</td>
                    <td className="px-4 py-1.5 text-right">{formatAmount(entry.totalCredit)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ))
        )}

        {/* Totaux généraux */}
        {grandTotals && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 flex justify-between items-center">
            <div>
              <span className="font-bold text-foreground">Totaux généraux</span>
              <span className="ml-4 text-sm text-muted-foreground">
                {grandTotals.journalCount || 0} écriture(s) · {grandTotals.lineCount || 0} ligne(s)
              </span>
            </div>
            <div className="flex gap-8 font-semibold">
              <span>Débit : {formatAmount(grandTotals.totalDebit)}</span>
              <span>Crédit : {formatAmount(grandTotals.totalCredit)}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderAgedBalance = () => {
    const metadata = getObject(data, 'metadata');
    const buckets = getObject(data, 'buckets');
    const details = getArray(data, 'details');
    const summary = getObject(data, 'summary');
    const isClients = metadata.type === 'clients' || type === 'aged-receivables';
    const title = isClients ? 'Balance Âgée Clients' : 'Balance Âgée Fournisseurs';

    const bucketOrder = ['current', 'b0_30', 'b31_60', 'b61_90', 'over90'];
    const bucketLabels: Record<string, string> = {
      current: 'Courant',
      b0_30: '0-30 jours',
      b31_60: '31-60 jours',
      b61_90: '61-90 jours',
      over90: '+90 jours',
    };

    return (
      <div className="space-y-4">
        {/* En-tête */}
        <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
          <h3 className="text-lg font-bold text-amber-900 dark:text-amber-100">{title}</h3>
          {metadata.asOfDate && (
            <p className="text-sm text-amber-700 dark:text-amber-300">
              Au {new Date(metadata.asOfDate).toLocaleDateString('fr-FR')} · Total : {formatAmount(summary.totalAmount)} ({summary.totalCount || 0} lignes)
            </p>
          )}
        </div>

        {/* Cartes par tranche */}
        <div className="grid grid-cols-5 gap-3">
          {bucketOrder.map(key => {
            const bucket = buckets?.[key];
            if (!bucket) return null;
            return (
              <div key={key} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-center">
                <p className="text-xs text-muted-foreground mb-1">{bucketLabels[key]}</p>
                <p className="text-lg font-bold text-foreground">{formatAmount(bucket.amount)}</p>
                <p className="text-xs text-muted-foreground">{bucket.count} ligne(s)</p>
              </div>
            );
          })}
        </div>

        {/* Tableau détaillé */}
        {details.length > 0 && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left px-4 py-2 font-medium">Date</th>
                  <th className="text-left px-4 py-2 font-medium">Compte</th>
                  <th className="text-left px-4 py-2 font-medium">Libellé</th>
                  <th className="text-right px-4 py-2 font-medium">Solde</th>
                  <th className="text-right px-4 py-2 font-medium">Jours</th>
                  <th className="text-center px-4 py-2 font-medium">Tranche</th>
                </tr>
              </thead>
              <tbody>
                {details.map((d: any, idx: number) => (
                  <tr key={idx} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="px-4 py-1.5 text-xs">{d.date ? new Date(d.date).toLocaleDateString('fr-FR') : ''}</td>
                    <td className="px-4 py-1.5 font-mono text-xs">{d.accountCode}</td>
                    <td className="px-4 py-1.5">{d.description || d.accountName}</td>
                    <td className="px-4 py-1.5 text-right font-semibold">{formatAmount(d.balance)}</td>
                    <td className="px-4 py-1.5 text-right">{d.daysPastDue}</td>
                    <td className="px-4 py-1.5 text-center">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700">{bucketLabels[d.bucket] || d.bucket}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderGeneralLedger = () => {
    const period = getObject(data, 'period');
    const accountsList = getArray(data, 'accounts');
    const grandTotals = getObject(data, 'grandTotals');

    return (
      <div className="space-y-4">
        {/* En-tête */}
        <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-200 dark:border-indigo-800">
          <h3 className="text-lg font-bold text-indigo-900 dark:text-indigo-100">Grand Livre</h3>
          {period.startDate && period.endDate && (
            <p className="text-sm text-indigo-700 dark:text-indigo-300">
              Période : {new Date(period.startDate).toLocaleDateString('fr-FR')} — {new Date(period.endDate).toLocaleDateString('fr-FR')}
              {' · '}{grandTotals.accountCount || 0} comptes · {grandTotals.entryCount || 0} écritures
            </p>
          )}
        </div>

        {accountsList.length === 0 ? (
          <p className="text-gray-500 italic text-center py-4">Aucun compte pour cette période</p>
        ) : (
          accountsList.map((acct: any, idx: number) => {
            const account = acct.account || {};
            const entries = getArray(acct, 'entries');
            const totals = getObject(acct, 'totals');
            return (
              <div key={account.id || idx} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                {/* En-tête du compte */}
                <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 flex justify-between items-center">
                  <div>
                    <span className="font-mono text-sm font-semibold text-foreground">{account.code}</span>
                    <span className="ml-3 text-sm text-foreground">{account.name}</span>
                    <span className="ml-3 text-xs text-muted-foreground uppercase">{account.type}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{acct.entryCount || entries.length} écriture(s)</span>
                </div>
                {/* Lignes */}
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800/50">
                      <th className="text-left px-4 py-1.5 font-medium">Date</th>
                      <th className="text-left px-4 py-1.5 font-medium">Référence</th>
                      <th className="text-left px-4 py-1.5 font-medium">Libellé</th>
                      <th className="text-right px-4 py-1.5 font-medium">Débit</th>
                      <th className="text-right px-4 py-1.5 font-medium">Crédit</th>
                      <th className="text-right px-4 py-1.5 font-medium">Solde</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e: any, eIdx: number) => (
                      <tr key={eIdx} className="border-b border-gray-100 dark:border-gray-800">
                        <td className="px-4 py-1.5 text-xs">{e.date ? new Date(e.date).toLocaleDateString('fr-FR') : ''}</td>
                        <td className="px-4 py-1.5 font-mono text-xs">{e.reference || e.journalNumber || ''}</td>
                        <td className="px-4 py-1.5">{e.description}</td>
                        <td className="px-4 py-1.5 text-right">{e.debit ? formatAmount(e.debit) : ''}</td>
                        <td className="px-4 py-1.5 text-right">{e.credit ? formatAmount(e.credit) : ''}</td>
                        <td className="px-4 py-1.5 text-right font-semibold">{formatAmount(e.runningBalance)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 dark:bg-gray-800 font-semibold">
                      <td colSpan={3} className="px-4 py-1.5 text-right">Total</td>
                      <td className="px-4 py-1.5 text-right">{formatAmount(totals.debit)}</td>
                      <td className="px-4 py-1.5 text-right">{formatAmount(totals.credit)}</td>
                      <td className="px-4 py-1.5 text-right">{formatAmount(totals.closingBalance ?? totals.normalizedBalance)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            );
          })
        )}

        {/* Totaux généraux */}
        {grandTotals && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 flex justify-between items-center">
            <div>
              <span className="font-bold text-foreground">Totaux généraux</span>
              <span className="ml-4 text-sm text-muted-foreground">
                {grandTotals.accountCount || 0} compte(s) · {grandTotals.entryCount || 0} écriture(s)
              </span>
            </div>
            <div className="flex gap-8 font-semibold">
              <span>Débit : {formatAmount(grandTotals.totalDebit)}</span>
              <span>Crédit : {formatAmount(grandTotals.totalCredit)}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  switch (type) {
    case 'balance':
      return renderBalanceSheet();
    case 'income':
      return renderIncomeStatement();
    case 'cashflow':
      return renderCashFlow();
    case 'equity-changes':
      return renderEquityChanges();
    case 'notes':
      return renderNotes();
    case 'trial-balance':
      return <TrialBalance data={data} currency={currency} />;
    case 'journal-book':
      return renderJournalBook();
    case 'general-ledger':
      return renderGeneralLedger();
    case 'aged-receivables':
    case 'aged-payables':
      return renderAgedBalance();
    default:
      return renderGenericReport();
  }
}